//src/pages/reports/profesores/OcupacionProfesorPage.tsx
import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Stack,
  TextField,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Slider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useOcupacionProfesorReport } from '../../../hooks/useReportJob';
import { useProfesores } from '../../../hooks/useProfesores';
import { useCentros } from '../../../hooks/useCentros';
import { useTipos } from '../../../hooks/useTiposAsignatura';
import { downloadReportFile } from '../../../components/utils/downloadXlsx';

export default function OcupacionProfesorPage() {
  const { t } = useTranslation<'translation'>();
  /* ─── filtros básicos ─── */
  const hoy = dayjs().format('YYYY-MM-DD');
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [profSel, setProfSel] = useState<{ id: number; nombre: string } | null>(null);
  const [centroSel, setCentSel] = useState<{
    id: number;
    nombre: string;
  } | null>(null);
  const [tipoSel, setTipoSel] = useState<{ id: number; nombre: string } | null>(null);
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFin, setHoraFin] = useState('');
  const [filtroOcupacionMin, setFiltroOcupacionMin] = useState(0);
  const [filtroOcupacionMax, setFiltroOcupacionMax] = useState(200);
  const [mostrarSoloSobreOcupados, setMostrarSoloSobreOcupados] = useState(false);
  const [vistaTabla, setVistaTabla] = useState(true);

  /* ─── datos catálogos ─── */
  const { rows: profesores } = useProfesores();
  const { rows: centros } = useCentros();
  const { rows: tipos } = useTipos();

  /* ─── ejecución informe ─── */
  const [jobId, setJobId] = useState<string>();
  const { run, useStatus } = useOcupacionProfesorReport();
  const { data: status } = useStatus(jobId);

  const lanzar = async () => {
    const horaInicioVal = horaInicio || undefined;
    const horaFinVal = horaFin || undefined;
    const { jobId } = await run({
      desde,
      hasta,
      profesorId: profSel?.id,
      centroId: centroSel?.id,
      tipoId: tipoSel?.id,
      ...((horaInicioVal || horaFinVal) ? { horaInicio: horaInicioVal, horaFin: horaFinVal } : {}),
    });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  /* ─── datos procesados ─── */
  const datosGraf = status?.result?.rowsWeek ?? [];
  const datosProfesores = useMemo(() => {
    let filtered = status?.result?.rowsProfesor ?? [];

    // Filtrar por rango de ocupación
    filtered = filtered.filter((r) => r.pct >= filtroOcupacionMin && r.pct <= filtroOcupacionMax);

    // Filtrar solo sobreocupados si está activo
    if (mostrarSoloSobreOcupados) {
      filtered = filtered.filter((r) => r.pct > 100);
    }

    return filtered.sort((a, b) => b.pct - a.pct);
  }, [
    status?.result?.rowsProfesor,
    filtroOcupacionMin,
    filtroOcupacionMax,
    mostrarSoloSobreOcupados,
  ]);

  /* ─── función para color según porcentaje ─── */
  const getColorPorOcupacion = (pct: number) => {
    if (pct < 80) return '#4caf50'; // verde
    if (pct <= 95) return '#ff9800'; // amarillo
    return '#f44336'; // rojo
  };

  /* ─── datos para gráfica de barras ─── */
  const datosBarras = useMemo(() => {
    return datosProfesores.slice(0, 15).map((r) => ({
      profesor: r.profesor,
      'Horas Máx': r.horasMax,
      'Horas Reales': r.horasReales,
      pct: r.pct,
    }));
  }, [datosProfesores]);

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t('ocupacionProfesor.titulo')}</Typography>

      {/* filtros */}
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <TextField
          type="date"
          label={t('ocupacionProfesor.filtros.desde')}
          value={desde}
          onChange={(e) => setDesde(e.target.value)}
        />
        <TextField
          type="date"
          label={t('ocupacionProfesor.filtros.hasta')}
          value={hasta}
          onChange={(e) => setHasta(e.target.value)}
        />

        <Autocomplete
          options={profesores}
          getOptionLabel={(o) => o.nombre}
          value={profSel}
          onChange={(_, v) => setProfSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t('ocupacionProfesor.filtros.profesor')} size="small" />
          )}
          sx={{ width: 220 }}
        />
        <Autocomplete
          options={centros.filter((c) => c.nombre !== 'OTHER')}
          getOptionLabel={(o) => o.nombre}
          value={centroSel}
          onChange={(_, v) => setCentSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t('ocupacionProfesor.filtros.centro')} size="small" />
          )}
          sx={{ width: 220 }}
        />
        <Autocomplete
          options={tipos}
          getOptionLabel={(o) => o.nombre}
          value={tipoSel}
          onChange={(_, v) => setTipoSel(v)}
          renderInput={(p) => (
            <TextField {...p} label={t('ocupacionProfesor.filtros.tipo')} size="small" />
          )}
          sx={{ width: 220 }}
        />

        <TextField
          type="time"
          label={t('ocupacionProfesor.filtros.horaInicio', 'Hora inicio')}
          value={horaInicio}
          onChange={(e) => setHoraInicio(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }}
        />
        <TextField
          type="time"
          label={t('ocupacionProfesor.filtros.horaFin', 'Hora fin')}
          value={horaFin}
          onChange={(e) => setHoraFin(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }}
        />

        <Button variant="contained" onClick={lanzar}>
          {t('ocupacionProfesor.filtros.generar')}
        </Button>
      </Stack>

      {/* progreso */}
      {status && !status.done && !status.failed && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t('ocupacionProfesor.estado.generando', {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* resultado */}
      {status?.done && (
        <>
          <Alert severity="success">
            {t('ocupacionProfesor.estado.exito')}{' '}
            {status.result!.url && (
              <Button
                variant="text"
                size="small"
                onClick={handleDownload}
                sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
              >
                {t('ocupacionProfesor.estado.descargar')}
              </Button>
            )}{' '}
            {t('ocupacionProfesor.estado.registros', {
              count: status.result!.registros,
            })}
          </Alert>

          {/* Filtros adicionales */}
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Filtros Avanzados</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 300 }}>
                  <Typography variant="body2">Ocupación mínima: {filtroOcupacionMin}%</Typography>
                  <Slider
                    value={filtroOcupacionMin}
                    onChange={(_, v) => setFiltroOcupacionMin(v as number)}
                    min={0}
                    max={200}
                    step={5}
                  />
                </Box>
                <Box sx={{ width: 300 }}>
                  <Typography variant="body2">Ocupación máxima: {filtroOcupacionMax}%</Typography>
                  <Slider
                    value={filtroOcupacionMax}
                    onChange={(_, v) => setFiltroOcupacionMax(v as number)}
                    min={0}
                    max={200}
                    step={5}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mostrarSoloSobreOcupados}
                      onChange={(e) => setMostrarSoloSobreOcupados(e.target.checked)}
                    />
                  }
                  label="Solo sobreocupados (>100%)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={vistaTabla}
                      onChange={(e) => setVistaTabla(e.target.checked)}
                    />
                  }
                  label="Vista tabla"
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Gráfica de evolución semanal */}
          <Paper sx={{ height: 400, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Evolución Semanal
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={datosGraf}>
                <XAxis dataKey="fecha" />
                <YAxis domain={[0, 150]} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    if (name === 'pct') return `${value}%`;
                    return value;
                  }}
                  labelFormatter={(label) => `Semana: ${label}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="pct"
                  name="Ocupación %"
                  strokeWidth={2}
                  dot={false}
                  stroke="#1976d2"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* Gráfica de barras comparativa */}
          {datosBarras.length > 0 && (
            <Paper sx={{ height: 500, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Comparativa Horas (Top 15)
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={datosBarras} layout="vertical">
                  <XAxis type="number" />
                  <YAxis dataKey="profesor" type="category" width={150} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Horas Máx" fill="#90caf9" />
                  <Bar dataKey="Horas Reales" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* Tabla completa por profesor */}
          {vistaTabla && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detalle por Profesor ({datosProfesores.length} profesores)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Profesor</TableCell>
                      <TableCell align="right">Horas Máx</TableCell>
                      <TableCell align="right">Horas Reales</TableCell>
                      <TableCell align="right">Horas Disponibles</TableCell>
                      <TableCell align="right">Ocupación %</TableCell>
                      <TableCell align="right">Promedio %</TableCell>
                      <TableCell align="right">Semanas</TableCell>
                      <TableCell align="right">Semanas &gt;100%</TableCell>
                      <TableCell align="right">Online</TableCell>
                      <TableCell align="right">Presencial</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {datosProfesores.map((r) => (
                      <TableRow key={r.profesorId}>
                        <TableCell>{r.profesor}</TableCell>
                        <TableCell align="right">{r.horasMax.toFixed(1)}</TableCell>
                        <TableCell align="right">{r.horasReales.toFixed(1)}</TableCell>
                        <TableCell align="right">{r.horasDisponibles.toFixed(1)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${r.pct.toFixed(1)}%`}
                            size="small"
                            sx={{
                              backgroundColor: getColorPorOcupacion(r.pct),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{r.promedioPct.toFixed(1)}%</TableCell>
                        <TableCell align="right">{r.semanasCompletas}</TableCell>
                        <TableCell align="right">
                          {r.semanasSobreOcupacion > 0 ? (
                            <Chip label={r.semanasSobreOcupacion} size="small" color="error" />
                          ) : (
                            0
                          )}
                        </TableCell>
                        <TableCell align="right">{r.horasOnline.toFixed(1)}h</TableCell>
                        <TableCell align="right">{r.horasPresencial.toFixed(1)}h</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Vista rápida con chips (si no está en tabla) */}
          {!vistaTabla && (
            <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {datosProfesores.slice(0, 20).map((r) => (
                <Chip
                  key={r.profesorId}
                  label={`${r.profesor}: ${r.pct.toFixed(1)}%`}
                  sx={{
                    backgroundColor: getColorPorOcupacion(r.pct),
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              ))}
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}
